import React, { useState, useEffect } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Paper,
  Chip,
  Grid,
  CircularProgress,
  Avatar,
  Link,
  Alert,
  AlertTitle,
} from '@mui/material'
import { formatDistanceToNowStrict } from 'date-fns'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SendIcon from '@mui/icons-material/Send'
import axios from 'axios'
import * as Sentry from '@sentry/react'
import { FEEDBACK_CATEGORIES } from '../../types/feedbackCategory'

interface Comment {
  id: number
  comment: string
  created_at: string
  from_user: string
  from_user_auth0_id?: string
  entity_id?: string
  entity_table?: string
}

interface FeedbackDetail {
  id: number
  subject: string
  message: string
  status: 'new' | 'in_progress' | 'resolved' | 'no_action'
  created_at: string
  updated_at: string
  user_name: string
  assigned_name: string | null
  assigned_to: string | null
  funder_name: string
  opportunity_name: string
  rank: number
  comments: Comment[]
  scan_result_id: number | null
  user_uuid: string
  org_uuid: string
  hidden: boolean
  category: string | null
  consent_to_share: boolean | null
}

interface AdminUser {
  id: string
  name: string
}

interface SubmitterUser {
  uuid: string
  name: string
  email: string
}

interface OrgPreference {
  id: number
  need_id: number | null
  preference_text: string
  updated_at: string | null
}

interface OrgDetail {
  id: string
  name: string
  match_preferences?: OrgPreference[]
}

// Represents the data structure from /admin/scans/{id}
interface ScanResultFromApi {
  id: number
  need_id: number // ID of the funding need
  org_uuid: string // UUID of the organisation
  result: string // The match result string from the scan (can be long)
  funding_need_title: string // Direct title from the scan result
  funder_uuid: string // UUID of the funder
  opportunity_uuid: string // UUID of the opportunity
  // ... other fields from scan result if needed
}

// Real API calls
const fetchFeedbackDetail = async (id: string): Promise<FeedbackDetail> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL
    const response = await axios.get(`${apiUrl}/feedback/${id}`)
    return response.data
  } catch (error: any) {
    console.error('Error fetching feedback details:', error)
    throw error
  }
}

// Get admin users
const fetchAdminUsers = async (): Promise<AdminUser[]> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL

    // VITE_API_URL already includes '/v1/admin', so the full path is '/v1/admin/users?role=admin'
    // Using the direct role parameter since it's simpler and matches our backend implementation
    const response = await axios.get(`${apiUrl}/users/?role=admin`)

    if (!response.data || !Array.isArray(response.data)) {
      console.error('Unexpected response format:', response.data)
      return []
    }

    // Map the response data to the AdminUser format
    return response.data.map((user: any) => ({
      id: user.uuid,
      name:
        `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
    }))
  } catch (error: any) {
    console.error('Error fetching admin users:', error)
    // Log more details about the error
    if (error.response) {
      console.error('Response status:', error.response.status)
      console.error('Response data:', error.response.data)
    }
    return []
  }
}

// Add comment to feedback
const addComment = async (
  feedbackId: number,
  comment: string,
): Promise<Comment> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL
    const response = await axios.post(`${apiUrl}/feedback/comment`, {
      feedback_id: feedbackId,
      comment,
    })
    return response.data
  } catch (error) {
    console.error('Error adding comment:', error)
    throw error
  }
}

// Update feedback
const updateFeedback = async (
  id: number,
  updates: { status?: string; assigned_to?: string | null; category?: string },
): Promise<boolean> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL
    const response = await axios.patch(`${apiUrl}/feedback/${id}`, updates)

    if (response.status !== 200) {
      throw new Error('Failed to update feedback')
    }

    return true
  } catch (error) {
    console.error('Error updating feedback:', error)
    throw error
  }
}

const fetchOrgDetail = async (orgId: string): Promise<OrgDetail | null> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL
    // Ensure the orgId is not undefined or null before making the call
    if (!orgId) {
      console.warn('fetchOrgDetail called with invalid orgId:', orgId)
      return null
    }
    const response = await axios.get(`${apiUrl}/orgs/${orgId}`)
    if (response.data && response.data.name) {
      return response.data
    } else {
      console.warn(
        `Organisation data or name not found for ID ${orgId}:`,
        response.data,
      )
      return null // Or return a default OrgDetail object with a placeholder name
    }
  } catch (error: any) {
    console.error(`Error fetching organisation details for ID ${orgId}:`, error)
    // Consider not throwing here to prevent unhandled promise rejections if not caught upstream
    // For example, return null and let the UI handle it
    return null
  }
}

const fetchSubmitterUser = async (
  userId: string,
): Promise<SubmitterUser | null> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL
    if (!userId) {
      console.warn('fetchSubmitterUser called with invalid userId:', userId)
      return null
    }
    const response = await axios.get(`${apiUrl}/users/${userId}`) // Assuming endpoint for single user
    if (response.data) {
      const user = response.data
      const name =
        `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email
      return {
        uuid: user.uuid,
        name: name,
        email: user.email,
      }
    } else {
      console.warn(`User data not found for ID ${userId}:`, response.data)
      return null
    }
  } catch (error: any) {
    console.error(
      `Error fetching submitter user details for ID ${userId}:`,
      error,
    )
    return null
  }
}

// Fetches data from /admin/scans/{scanResultId}
const fetchScanData = async (
  scanResultId: number,
): Promise<ScanResultFromApi | null> => {
  try {
    const apiUrl = import.meta.env.VITE_API_URL
    if (!scanResultId) {
      console.warn(
        'fetchScanData called with invalid scanResultId:',
        scanResultId,
      )
      return null
    }
    // apiUrl (VITE_API_URL) is assumed to already include /admin if necessary, per dataProvider.ts
    const response = await axios.get(`${apiUrl}/scans/${scanResultId}`)
    if (response.data) {
      return response.data as ScanResultFromApi
    } else {
      console.warn(`Scan data not found for ID ${scanResultId}:`, response.data)
      return null
    }
  } catch (error: any) {
    console.error(`Error fetching scan data for ID ${scanResultId}:`, error)
    return null
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'new':
      return 'error'
    case 'in_progress':
      return 'warning'
    case 'resolved':
      return 'success'
    case 'no_action':
      return 'default'
    default:
      return 'default'
  }
}

const getInitials = (
  name: string | null | undefined,
  email?: string | null | undefined,
) => {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
  }
  if (email) {
    return email
  }
  return '' // Fallback if no name and no email or invalid email
}

export const FeedbackDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [feedback, setFeedback] = useState<FeedbackDetail | null>(null)
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState<string>('')
  const [assignedTo, setAssignedTo] = useState<string>('')
  const [category, setCategory] = useState<string>('')
  const [commentText, setCommentText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [orgName, setOrgName] = useState<string | null>(null)
  const [orgPreferences, setOrgPreferences] = useState<OrgPreference[]>([])
  const [orgNameLoading, setOrgNameLoading] = useState<boolean>(false)
  const [submitterInfo, setSubmitterInfo] = useState<SubmitterUser | null>(null)
  const [submitterLoading, setSubmitterLoading] = useState<boolean>(false)
  const [fundingNeedName, setFundingNeedName] = useState<string | null>(null)
  const [fundingNeedNameLoading, setFundingNeedNameLoading] =
    useState<boolean>(false)
  const [scanResultOutput, setScanResultOutput] = useState<string | null>(null)
  const [funderUuid, setFunderUuid] = useState<string | null>(null)
  const [opportunityUuid, setOpportunityUuid] = useState<string | null>(null)
  const [needId, setNeedId] = useState<number | null>(null)
  const [generalError, setGeneralError] = useState<string | null>(null)

  // Load admin users on component mount
  useEffect(() => {
    const loadAdminUsers = async () => {
      const users = await fetchAdminUsers()
      setAdminUsers(users)
    }

    loadAdminUsers()
  }, [])

  useEffect(() => {
    const loadFeedback = async () => {
      if (!id) return

      setLoading(true)
      try {
        const data = await fetchFeedbackDetail(id)
        setFeedback(data)
        setStatus(data.status)
        setAssignedTo(data.assigned_to || '')
        setCategory(data.category || '')
        setGeneralError(null) // Clear previous errors
      } catch (error: any) {
        console.error('Error loading feedback:', error)
        Sentry.captureException(error)
        setGeneralError(
          `Failed to load feedback details: ${
            error.message || 'Unknown error'
          }`,
        )
      } finally {
        setLoading(false)
      }
    }

    loadFeedback()
  }, [id])

  useEffect(() => {
    if (feedback?.org_uuid) {
      setOrgNameLoading(true)
      setOrgName(null)
      fetchOrgDetail(feedback.org_uuid)
        .then((data) => {
          if (data && data.name) {
            setOrgName(data.name)
            setOrgPreferences(data.match_preferences || [])
          } else {
            setOrgName('Organisation name not available')
          }
        })
        .catch(() => {
          setOrgName('Failed to load organisation')
        })
        .finally(() => {
          setOrgNameLoading(false)
        })
    } else {
      setOrgName(null)
      setOrgNameLoading(false)
    }
  }, [feedback?.org_uuid])

  useEffect(() => {
    if (feedback?.user_uuid) {
      setSubmitterLoading(true)
      setSubmitterInfo(null)
      fetchSubmitterUser(feedback.user_uuid)
        .then((data) => {
          setSubmitterInfo(data)
        })
        .catch(() => {
          setSubmitterInfo(null)
        })
        .finally(() => {
          setSubmitterLoading(false)
        })
    } else {
      setSubmitterInfo(null)
      setSubmitterLoading(false)
    }
  }, [feedback?.user_uuid])

  useEffect(() => {
    if (feedback?.scan_result_id && feedback.funder_name) {
      setFundingNeedNameLoading(true)
      setFundingNeedName(null)
      setScanResultOutput(null)
      setFunderUuid(null)
      setOpportunityUuid(null)

      fetchScanData(feedback.scan_result_id)
        .then((scanData) => {
          if (scanData) {
            // Set scan match result (assuming it's a string now, adjust if it's truly boolean sometimes)
            // For display purposes, we might not need to store the full 'result' string in state
            // if it's very long and only used for a boolean check elsewhere.
            // However, the screenshot shows it as a string.
            // For now, let's assume we don't need to set scanMatchResult from scanData.result directly
            // unless there's a specific UI element that displays this string.
            // The original code was trying to set a boolean scanMatchResult.
            // If scanData.result was intended for a boolean match display, that logic needs clarification.
            // For now, focusing on funding_need_title.

            if (scanData.funding_need_title) {
              setFundingNeedName(scanData.funding_need_title)
            } else {
              setFundingNeedName(
                'Funding Need: N/A (title not provided in scan data)',
              )
            }

            if (scanData.result) {
              setScanResultOutput(scanData.result)
            } else {
              setScanResultOutput('Scan Result: Not available')
            }

            if (scanData.funder_uuid) {
              setFunderUuid(scanData.funder_uuid)
            }
            if (scanData.opportunity_uuid) {
              setOpportunityUuid(scanData.opportunity_uuid)
            }
            if (scanData.need_id) {
              setNeedId(scanData.need_id)
            }
          } else {
            console.warn(
              'Scan data is null for scan_result_id:',
              feedback.scan_result_id,
            )
            setFundingNeedName('Funding Need: N/A (scan data not found)')
            setScanResultOutput(null)
            setFunderUuid(null)
            setOpportunityUuid(null)
          }
        })
        .catch((error) => {
          console.error('Error fetching or processing scan data:', error)
          setFundingNeedName('Funding Need: Error loading details')
          setScanResultOutput(null)
          setFunderUuid(null)
          setOpportunityUuid(null)
        })
        .finally(() => {
          setFundingNeedNameLoading(false)
        })
    } else {
      setFundingNeedName(null)
      setFundingNeedNameLoading(false)
      setScanResultOutput(null)
      setFunderUuid(null)
      setOpportunityUuid(null)
    }
  }, [feedback?.scan_result_id, feedback?.funder_name])

  const handleStatusChange = (newStatus: string) => {
    setSaved(false)
    setStatus(newStatus)
  }

  const handleAssigneeChange = (newAssignee: string) => {
    setSaved(false)
    setAssignedTo(newAssignee)
  }
  const handleCategoryChange = (newCategory: string) => {
    setSaved(false)
    setCategory(newCategory)
  }

  const handleSaveChanges = async () => {
    if (!feedback) return

    setSaved(false)
    setSaving(true)
    try {
      // If assignedTo is an empty string, explicitly set it to null
      const assigned = assignedTo === '' ? null : assignedTo

      const success = await updateFeedback(feedback.id, {
        status: status,
        assigned_to: assigned,
        category,
      })

      if (!success) {
        throw new Error('Failed to save changes')
      } else {
        setSaved(true)
      }

      // Reload feedback from server to get updated data
      const updatedFeedback = await fetchFeedbackDetail(id || '')
      setFeedback(updatedFeedback)
      setGeneralError(null) // Clear previous errors
    } catch (error: any) {
      console.error('Error saving changes:', error)
      Sentry.captureException(error)
      setGeneralError(
        `Failed to save changes: ${error.message || 'Unknown error'}`,
      )
    } finally {
      setSaving(false)
    }
  }

  const handleAddComment = async () => {
    if (!feedback || !commentText.trim()) return

    setSubmitting(true)
    try {
      await addComment(feedback.id, commentText)

      // Reload feedback from server to get updated comments
      const updatedFeedback = await fetchFeedbackDetail(id || '')
      setFeedback(updatedFeedback)

      // Clear the comment input
      setCommentText('')
      setGeneralError(null) // Clear previous errors
    } catch (error: any) {
      console.error('Error adding comment:', error)
      Sentry.captureException(error)
      setGeneralError(
        `Failed to add comment: ${error.message || 'Unknown error'}`,
      )
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '50vh',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!feedback) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5">Feedback not found</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/feedback')}
          sx={{ mt: 2 }}
        >
          Back to List
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4">Feedback #{feedback.id}</Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/feedback')}
        >
          Back to List
        </Button>
      </Box>

      {generalError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setGeneralError(null)}
        >
          <AlertTitle>Error</AlertTitle>
          {generalError}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          {/* Feedback details */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {feedback.subject}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={feedback.status.replace('_', ' ')}
                  color={getStatusColor(feedback.status) as any}
                  size="small"
                />
                <Typography variant="caption" sx={{ ml: 2 }}>
                  Submitted{' '}
                  {formatDistanceToNowStrict(
                    new Date(feedback.created_at + 'Z'),
                    {
                      addSuffix: true,
                    },
                  )}
                </Typography>
              </Box>
              <Typography
                variant="body1"
                sx={{ mb: 2, whiteSpace: 'pre-wrap' }}
              >
                {feedback.message}
              </Typography>
              <Divider sx={{ my: 2 }} />
              {feedback.funder_name ? (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    Match Details
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Funder:</strong>{' '}
                    {funderUuid ? (
                      <Link
                        component={RouterLink}
                        to={`/funders/${funderUuid}`}
                      >
                        {feedback.funder_name}
                      </Link>
                    ) : (
                      feedback.funder_name
                    )}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    <strong>Opportunity:</strong>{' '}
                    {opportunityUuid ? (
                      <Link
                        component={RouterLink}
                        to={`/opportunities/${opportunityUuid}`}
                      >
                        {feedback.opportunity_name}
                      </Link>
                    ) : (
                      feedback.opportunity_name
                    )}
                  </Typography>
                  {/* Display Funding Need Name */}
                  {/* Existing Funding Need Name Logic */}
                  {fundingNeedNameLoading ? (
                    <CircularProgress size={20} sx={{ my: 1 }} />
                  ) : fundingNeedName ? (
                    <Typography variant="body2" gutterBottom>
                      <strong>Funding Need:</strong> {fundingNeedName}
                    </Typography>
                  ) : feedback.funder_name &&
                    feedback.scan_result_id &&
                    !fundingNeedNameLoading ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      <strong>Funding Need:</strong> Information unavailable
                    </Typography>
                  ) : feedback.funder_name &&
                    !feedback.scan_result_id &&
                    !fundingNeedNameLoading ? (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      gutterBottom
                    >
                      <strong>Funding Need:</strong> Not linked to a scan result
                    </Typography>
                  ) : null}
                  {/* Display Scan Result Output */}
                  {scanResultOutput && (
                    <Typography
                      variant="body2"
                      gutterBottom
                      sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                    >
                      <strong>Scan Result:</strong> {scanResultOutput}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    <strong>Match Quality:</strong> {feedback.rank}/5
                  </Typography>
                </>
              ) : (
                <Typography variant="body2" fontWeight={600}>
                  General Feedback
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Comments section */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Comments
              </Typography>

              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  disabled={submitting}
                />
                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}
                >
                  <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={handleAddComment}
                    disabled={!commentText.trim() || submitting}
                  >
                    {submitting ? 'Sending...' : 'Add Comment'}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {feedback.comments.length === 0 ? (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ py: 2, textAlign: 'center' }}
                >
                  No comments yet
                </Typography>
              ) : (
                <Box sx={{ mt: 2 }}>
                  {feedback.comments.map((comment) => (
                    <Paper
                      key={comment.id}
                      variant="outlined"
                      sx={{ p: 2, mb: 2 }}
                    >
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
                      >
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            mr: 1,
                            bgcolor: 'primary.main',
                          }}
                        >
                          {getInitials(comment.from_user)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {comment.from_user}
                          </Typography>
                          <Typography variant="caption">
                            {formatDistanceToNowStrict(
                              new Date(comment.created_at + 'Z'),
                              {
                                addSuffix: true,
                              },
                            )}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-wrap' }}
                      >
                        {comment.comment}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          {/* Management panel */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Feedback Management
              </Typography>

              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="new">New</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="resolved">Resolved</MenuItem>
                    <MenuItem value="no_action">No Action</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel>Assign To</InputLabel>
                  <Select
                    value={assignedTo}
                    onChange={(e) => handleAssigneeChange(e.target.value)}
                    label="Assign To"
                  >
                    <MenuItem value="">Unassigned</MenuItem>
                    {adminUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {feedback.opportunity_name && feedback.hidden && (
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="">None</MenuItem>
                      {FEEDBACK_CATEGORIES.map((category) => (
                        <MenuItem key={category.value} value={category.value}>
                          {category.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              {saved ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Typography variant="body2">Changes Saved!</Typography>
                </Box>
              ) : (
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  onClick={handleSaveChanges}
                  disabled={saving || saved}
                >
                  {saving ? 'Saving Changes...' : 'Save Changes'}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Submitter info */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Submitter
              </Typography>
              {submitterLoading ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                      {getInitials(submitterInfo?.name, submitterInfo?.email)}
                    </Avatar>
                    <Typography>
                      {submitterInfo?.name ||
                        submitterInfo?.email ||
                        'Submitter info unavailable'}
                    </Typography>
                  </Box>
                  {feedback.consent_to_share && (
                    <Chip
                      label="Consents to Fundsorter anonymously sharing this feedback"
                      variant="outlined"
                      color="success"
                      size="small"
                      sx={{ mt: 2 }}
                    />
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Related Organisation info */}
          {feedback?.org_uuid && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Related Organisation
                </Typography>
                {orgNameLoading ? (
                  <CircularProgress size={24} />
                ) : orgName &&
                  !orgName.includes('Failed to load') &&
                  !orgName.includes('not available') ? (
                  <Link
                    component={RouterLink}
                    to={`/orgs/${feedback.org_uuid}`}
                  >
                    {orgName}
                  </Link>
                ) : orgName ? (
                  <Typography variant="body2" color="textSecondary">
                    {orgName}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    Organisation details unavailable.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Match Preferences */}
          {orgPreferences.length > 0 && (
            <Card sx={{ mt: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Match Preferences
                </Typography>

                {orgPreferences.filter((p) => p.need_id == null).length >
                  0 && (
                  <Box
                    sx={{
                      mb:
                        needId &&
                        orgPreferences.filter((p) => p.need_id === needId)
                          .length > 0
                          ? 2
                          : 0,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="textSecondary"
                      gutterBottom
                    >
                      Organisation-wide
                    </Typography>
                    {orgPreferences
                      .filter((p) => p.need_id == null)
                      .map((pref) => (
                        <Typography
                          key={pref.id}
                          variant="body2"
                          sx={{
                            p: 1,
                            mb: 0.5,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          {pref.preference_text}
                        </Typography>
                      ))}
                  </Box>
                )}

                {needId &&
                  orgPreferences.filter((p) => p.need_id === needId).length >
                    0 && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        gutterBottom
                      >
                        Funding Request
                      </Typography>
                      {orgPreferences
                        .filter((p) => p.need_id === needId)
                        .map((pref) => (
                          <Typography
                            key={pref.id}
                            variant="body2"
                            sx={{
                              p: 1,
                              mb: 0.5,
                              bgcolor: 'grey.50',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            {pref.preference_text}
                          </Typography>
                        ))}
                    </Box>
                  )}
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default FeedbackDetail
